import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
} from "@mui/material";
// import GoogleCalendarSvg from "../../assets/integration/google-calendar.svg";
// import SalesforceSvg from "../../assets/salesforce.svg";
// import ZoomSvg from "../../assets/integration/zoom.svg";
// import useIntegrationStyles from "./IntegrationStyles";
import CustomConfirm from "../../components/Common/CustomConfirm";
import ProgressSpinner from "../../components/Common/ProgressSpinner";
import IntegrationsAPIClient from "../../api/integrationsAPIs";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import useGlobalContext from "../../context/useGlobalContext";
import { IntegrationTypes } from "../../utils/types";
import { getUserAttributes, refreshCurrentSession } from "../../utils/auth";
import CalendarService from "../../services/calender/CalendarService";
import IntegrationService from "../../services/integrations/Integrations";
import CustomButton from "../Common/Button";
import GmailIcon from "../../assets/integration/Gmail_icon.svg";
import RoundedCheckicon from "../../assets/integration/rounded_check.svg";
import "../../styles/common.css";

interface IntegrationProps {
  userSettingsData?: any;
  isUserSettingsLoading?: boolean;
}

const Integration: React.FC<IntegrationProps> = ({ userSettingsData, isUserSettingsLoading = false }) => {
  const { setCalendarsData: setContextCalendarsData, accountDetails } = useGlobalContext();
  const { showSnackbar } = useDisplaySnackbar();
  const queryClient = useQueryClient();
  const [getCalendar, setGetCalendar] = useState(false);
  const [authoriseIntegrationType, setAuthoriseIntegrationType] = useState("");
  const [_integrationsList, setIntegrationsList] = useState<any | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [actionType, setActionType] = useState<
    "CONFIRMATION" | "CONNECTION" | "DELETE" | ""
  >("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [actionBtnLabel, setActionBtnLabel] = useState<string>("Confirm");

  const [selectedCalendar, setSelectedCalendar] = useState<{
    email_id: string;
    is_active: boolean;
    calendar_domain: IntegrationTypes;
  } | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState("");

  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  // const styles = useIntegrationStyles();
    const loggedInEmail = getUserAttributes()?.email;
    const authId = getUserAttributes()?.id;
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const loggedInUserSettings = userSettingsData?.data?.find((user: any) => user?.profileOrgId === loggedInProfileOrgId);
  const isGmailConnected = loggedInUserSettings?.isGmailConnected ?? false;

  const {
    isLoading: isIntegrationsLoading,
    isFetching: isIntegrationsFetching,
    isSuccess: isIntegrationsSuccess,
    isError: isIntegrationsError,
    error: errorIntegrations,
    data: IntegrationsData,
  } = CalendarService.useGetCalendarDetails({
    employeePorId: loggedInProfileOrgId || "",
  },
  getCalendar
);

  const {
    isLoading: isAuthoriseCalendarLoading,
    isFetching: isAuthoriseCalendarFetching,
    isSuccess: isAuthoriseCalendarSuccess,
    isError: isAuthoriseCalendarError,
    error: AuthoriseCalendarError,
    data: AuthoriseCalendarData,
  } = IntegrationService.useGetAuthorizeUrl(
    {
      userId: authId,
      employeeEmail: loggedInEmail,
    },
    authoriseIntegrationType === IntegrationTypes.GOOGLE ||
      authoriseIntegrationType === IntegrationTypes.ZOOM
      ? true
      : false
  );

  const {
    isLoading: isAuthoriseSalesforceLoading,
    isFetching: isAuthoriseSalesforceFetching,
    isSuccess: isAuthoriseSalesforceSuccess,
    isError: isAuthoriseSalesforceError,
    error: AuthoriseSalesforceError,
    data: AuthoriseSalesforceData,
  } = IntegrationsAPIClient.useAuthoriseSalesforce(
    // authoriseIntegrationType === IntegrationTypes.SALESFORCE
    //  // ? true
    //   : false
    false
  );

  const {
    mutate: mutateCalendarConnection,
    isSuccess: isCalendarConnectionSuccess,
    isError: isCalendarConnectionError,
    error: CalendarConnectionError,
  } = CalendarService.useConnectDisconnectCalendar({
    onMutate: () => {
      setShowSpinner(true);
    },
    onSettled: () => {
      setShowSpinner(false);
    },
  });

  const {
    mutate: mutateCalenderDelete,
    isSuccess: isCalenderDeleteSuccess,
    isError: isCalenderDeleteError,
    error: CalenderDeleteError,
  } = IntegrationsAPIClient.useCalendarDeleteMutation(
    selectedCalendar?.email_id || "",
    {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    }
  );

  const { mutate: updateUserSettings } = IntegrationService.useUpdateUserSettings({
    onSuccess: () => {
      showSnackbar("success", "Updated user settings");
      queryClient.invalidateQueries({
        queryKey: ["get_user_level_settings"],
      });
      queryClient.invalidateQueries({
        queryKey: ["get_calendar_details"],
      });
    },
    onError: () => {
      showSnackbar("error", "Failed to update user settings");
    },
  });

  const handleEmailChange = (profileOrgId: string, value: string) => {
    updateUserSettings({
      userSettings: [
        {
          profileOrgRelationshipId: profileOrgId,
          isEmailSyncEnabled: value === "enabled",
        },
      ],
    });
  };


  useEffect(() => {
    setIntegrationsList(null);
    if (isIntegrationsLoading || isIntegrationsFetching) {
      setShowSpinner(true);
    }
    if (!isIntegrationsLoading && !isIntegrationsFetching) {
      setShowSpinner(false);
    }
    if (isIntegrationsSuccess) {
      const result = IntegrationsData;
      setIntegrationsList(result);
  
      // Handle calendar sync presence (now a single object)
      const isActive = result?.isSyncEnabled ?? false;
      setContextCalendarsData(isActive ? [result] : []);
  
      localStorage.setItem("isPresentCalendar", isActive ? "true" : "false");
      setGetCalendar(false);
    }
    if (isIntegrationsError) {
      showSnackbar("error", errorIntegrations);
    }
  }, [
    isIntegrationsLoading,
    isIntegrationsFetching,
    isIntegrationsSuccess,
    isIntegrationsError,
  ]);

  // const renderIntegration = (
  //   email: string,
  //   is_active: boolean | null,
  //   last_fetch_date: string | null,
  //   integrationType: string,
  // ) => {
  //   let icon;
  //   let header = "";
  //   // let calendarNote = "";
  //   switch (integrationType) {
  //     case IntegrationTypes.GOOGLE:
  //       icon = (
  //         <img
  //           className={styles.integrationIcon}
  //           alt="Google Calendar"
  //           src={GoogleCalendarSvg}
  //         />
  //       );
  //       header = "Calendar Sync";
  //       // calendarNote = "Get reminders to prepare for your one on one meetings";
  //       break;
  //     case IntegrationTypes.ZOOM:
  //       icon = (
  //         <img className={styles.integrationIcon} alt="Zoom" src={ZoomSvg} />
  //       );
  //       header = "Zoom";
  //       // calendarNote = "Get reminders to prepare for your one on one meetings";
  //       break;
  //     default:
  //       // Provide a default case or handle unknown providers
  //       icon = null;
  //   }

  //   return (
  //     <div className={styles.integration}>
  //       <div className={styles.topSection}>
  //         <div className={styles.gCalendarParent}>
  //           {icon}
  //         </div>
  //         <div className={styles.calendarWrapper}>
  //           <div className={styles.calendarInfo}>
  //             <div className={styles.calendar}>{header}</div>
  //             <Typography sx={{ fontWeight: 500, color: "#6F6F7F" }}>
  //               {email}
  //             </Typography>
  //           </div>

  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  useEffect(() => {
    queryClient.resetQueries({ queryKey: ["get_authorize_url"] });
  }, []);  
  useEffect(() => {
    reset();
    if (isAuthoriseCalendarSuccess) {
      const redirectUrl: string =
        AuthoriseCalendarData?.authUrl || "";
      if (redirectUrl) window.location.href = redirectUrl;
      setAuthoriseIntegrationType("");
    }
    if (isAuthoriseCalendarError) {
      showSnackbar(
        "error",
        `Error while authorising Google calendar: ${AuthoriseCalendarError}`
      );
    }
  }, [
    isAuthoriseCalendarLoading,
    isAuthoriseCalendarFetching,
    isAuthoriseCalendarSuccess,
    isAuthoriseCalendarError,
  ]);

  useEffect(() => {
    reset();
    if (isAuthoriseSalesforceSuccess) {
      const redirectUrl: string =
        AuthoriseSalesforceData?.data?.data?.redirect_url || "";
      if (redirectUrl) window.location.href = redirectUrl;
      setAuthoriseIntegrationType("");
    }
    if (isAuthoriseCalendarError) {
      showSnackbar(
        "error",
        `Error while authorising Salesforce: ${AuthoriseSalesforceError}`
      );
    }
  }, [
    isAuthoriseSalesforceLoading,
    isAuthoriseSalesforceFetching,
    isAuthoriseSalesforceSuccess,
    isAuthoriseSalesforceError,
  ]);


  const handleConnection = () => {
    reset();
    if (selectedCalendar)
      mutateCalendarConnection({
        employeeEmail: selectedCalendar.email_id,
        isSyncEnabled: selectedCalendar.is_active,
      });
  };

  useEffect(() => {
    if (isCalendarConnectionSuccess) {
      queryClient.invalidateQueries({ queryKey: ["get_calendars"] });
      showSnackbar(
        "success",
        `Calendar ${
          selectedCalendar?.is_active ? "connected" : "disconnected"
        } successfully.`
      );
      setSelectedCalendar(null);
      setGetCalendar(true);
    }
    if (isCalendarConnectionError) {
      showSnackbar(
        "error",
        `Error while changing calendar connection: ${CalendarConnectionError}`
      );
    }
  }, [isCalendarConnectionSuccess, isCalendarConnectionError]);

  const handleDelete = () => {
    reset();
    if (selectedCalendar)
      mutateCalenderDelete({
        email_id: selectedCalendar.email_id,
        calendar_domain: selectedCalendar.calendar_domain,
      });
  };

  const updateAccessToken = async () => {
    try {
      setShowSpinner(true);
      await refreshCurrentSession();
    } finally {
      setShowSpinner(false);
    }
  };

  useEffect(() => {
    if (isCalenderDeleteSuccess) {
      updateAccessToken();
      queryClient.invalidateQueries({ queryKey: ["get_calendars"] });
      showSnackbar("success", `Calendar deleted successfully.`);
      setSelectedCalendar(null);
      setGetCalendar(true);
    }
    if (isCalenderDeleteError) {
      showSnackbar(
        "error",
        `Error while changing calendar connection: ${CalenderDeleteError}`
      );
    }
  }, [isCalenderDeleteSuccess, isCalenderDeleteError]);

  const reset = () => {
    setOpenDialog(false);
    setActionType("");
    setConfirmationMessage("");
  };

  const handleClose = () => {
    reset();
    setSelectedCalendar(null);
  };

  const handleIntegrationChange = (event: any) => {
    setSelectedIntegration(event.target.value);
  };

    const handleAddIntegration = () => {
      setActionType("CONFIRMATION");
      setConfirmationMessage(
        "By clicking <b>'Continue'</b>, you agree to allow <b>7thGear</b> (Product of Nowaat Inc.) to obtain and process the data using approved 3rd party AI applications required to deliver the capabilities of 7thGear. Data will be secured both in transit and storage as per the Privacy Policy."
      );
      setActionBtnLabel("Continue");
      setOpenDialog(true);
  };

  return (
    <>
      <Box className="flex flex-row justify-start items-center gap-2"
        sx={{ mt: 4 }}
      >
        {isGmailConnected ? (
          <div className="email-sync-card">
            <div className="email-sync-left">
              <div className="email-icon-container">
                <img src={GmailIcon} alt="Gmail" className="email-icon" />
              </div>

              <div className="email-content">
                <h2 className="email-title">Email Sync</h2>
                <p className="email-subtitle">Gmail / Workspace</p>
              </div>
            </div>

            <div className="email-divider"></div>

            <div className="email-status">
              <img
                src={RoundedCheckicon}
                alt="Active"
                className="status-icon"
              />
              <span className="status-text">Active</span>
            </div>
          </div>
        ) : (
          <Box className = "flex flex-row items-center justify-start gap-5">
            <FormControl sx={{ minWidth: 120, alignSelf: "center" }} size="small">
              <InputLabel id="calendar">Integration Type</InputLabel>
              <Select
                labelId="calendar"
                id="calendar-select"
                value={selectedIntegration}
                onChange={handleIntegrationChange}
                label="Integration Type"
                sx={{ minWidth: "180px" }}
                >
                <MenuItem value="" disabled>
                  Select an integration
                </MenuItem>
                <MenuItem value={IntegrationTypes.GOOGLE}>
                Gmail
              </MenuItem>
              </Select>
            </FormControl>
            <CustomButton
              type="SmallBrandGradient"
              clickHandler={() => {
                if (isGmailConnected) {
                  handleEmailChange(loggedInProfileOrgId, "disabled");
                  return;
                }
                handleAddIntegration();
              }}
              disabled={!isGmailConnected && selectedIntegration === ""}
              label= {isGmailConnected ? "Remove" : "Add"}
              /> 
          </Box>
        )}
      </Box>

      <CustomConfirm
        title="Confirmation"
        message={confirmationMessage}
        isOpen={openDialog}
        handleSuccess={() => {
          actionType === "CONFIRMATION" &&
            setAuthoriseIntegrationType(selectedIntegration);
          actionType === "CONNECTION" && handleConnection();
          actionType === "DELETE" && handleDelete();
        }}
        handleClose={handleClose}
        actionBtnLabel={actionBtnLabel}
      />
      <ProgressSpinner showSpinner={showSpinner || isUserSettingsLoading} />
    </>
  );
};

export default Integration;
